/**
 * Sprint 24.15 — agent template catalogue hooks.
 *
 * React Query wrappers over the member-grade MCP tools `list_agent_templates`
 * and `preview_agent_template`. The template types mirror the worker
 * (src/engine/templates.ts) and are kept in sync by hand — the worker owns the
 * catalogue; the console only ever reads it.
 */

import { useQuery } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";

export type TemplateRamp = "purple" | "teal" | "coral" | "pink" | "gray";
export type TemplateModelPolicy = "auto" | "economy" | "performance";
export type ModelTier = "Fast" | "Standard" | "Heavy";
/**
 * Sprint 24.15.1 — the customise-form tier choice. "Workspace" is the new
 * pre-selected default that maps to NO model_tier_override (the agent inherits
 * the workspace tier); the other three lock a specific tier.
 */
export type TierChoice = "Workspace" | ModelTier;

/** Sprint 24.15.2 — mirrors worker engine/templates.AvailabilityStatus. */
export type AvailabilityStatus = "first-party" | "generic-mcp" | "wrapped-api" | "coming-soon";

/** Sprint 24.15.1 — mirrors worker engine/templates.EquipmentType. */
export type EquipmentType =
  | "MCP connection"
  | "First-party connection"
  | "Wrapped API"
  | "Internal capability";

export interface TemplateEquipmentRequirement {
  kind: string;
  /** Sprint 24.15.1 — object type, surfaced as a pill in customise/preview rows. */
  equipment_type: EquipmentType;
  required: boolean;
  /** Sprint 24.15.2 — how this kind is installable today (drives status pills). */
  availability_status: AvailabilityStatus;
  /** Sprint 24.15.2 — e.g. "Sprint 24.17b" for coming-soon kinds. */
  availability_note?: string;
  purpose?: string;
}

export interface AgentTemplateSummary {
  id: string;
  source: "first-party" | "workspace";
  category: string;
  name: string;
  short_description?: string;
  icon?: string;
  ramp?: TemplateRamp;
  persona: string;
  default_model_policy?: TemplateModelPolicy | null;
  default_equipment: TemplateEquipmentRequirement[];
  starter_priorities: string[];
  starter_routines: { name: string; cadence: string }[];
  seed_memories: { type: string; body: string }[];
  authored_by: string;
  version: string;
}

export interface AgentTemplate extends AgentTemplateSummary {
  instructions: string;
}

export interface ListTemplatesOpts {
  category?: string;
  source?: "first-party" | "workspace";
}

export function useListAgentTemplates(opts: ListTemplatesOpts = {}) {
  const { category, source } = opts;
  return useQuery({
    queryKey: ["agent-templates", category ?? null, source ?? null],
    queryFn: async (): Promise<AgentTemplateSummary[]> => {
      const args: Record<string, unknown> = {};
      if (category) args.category = category;
      if (source) args.source = source;
      const res = await mcpClient.toolsCall<{ templates: AgentTemplateSummary[] }>(
        "list_agent_templates",
        args,
      );
      if (res.isError) throw new Error(res.content?.[0]?.text ?? "Couldn't load templates");
      return res.structuredContent?.templates ?? [];
    },
    staleTime: 5 * 60_000,
  });
}

export function usePreviewAgentTemplate(templateId: string | null) {
  return useQuery({
    queryKey: ["agent-template", templateId],
    enabled: !!templateId,
    queryFn: async (): Promise<AgentTemplate> => {
      const res = await mcpClient.toolsCall<{ template: AgentTemplate }>("preview_agent_template", {
        template_id: templateId,
      });
      if (res.isError || !res.structuredContent?.template) {
        throw new Error(res.content?.[0]?.text ?? "Couldn't load template");
      }
      return res.structuredContent.template;
    },
    staleTime: 5 * 60_000,
  });
}
